/**
 * MarkPeerMessageSeenUseCase
 *
 * Read-receipt write hop: flips `seenByPeer: true` on both the message doc
 * and the chat-node metadata doc, but ONLY when the incoming message still
 * carries `seenByPeer === false`. Already-seen messages short-circuit with
 * a no-op so the chat list's "every visible peer message" sweep on
 * `chat_screen.dart` doesn't issue redundant transactions for messages that
 * were marked seen on a previous render.
 *
 * Flutter source: lib/data/data_sources/chat_data_source.dart:100-126 —
 * `ChatDataSource.setPeerMessageSeen`. Flutter places the
 * `chatMessage.seenByPeer == false` guard inside the data source method, but
 * on web the data-layer hop (`firestoreService.setPeerMessageSeen`) is a
 * pure proxy that always issues the transaction; the guard moves UP to this
 * use case so the data layer stays primitive (data-layer.md surface rule)
 * and the no-op decision is observable at the domain layer.
 *
 * Pure-domain contract:
 *   - The use case does NOT touch `firestoreService` directly and does NOT
 *     dispatch to Redux — both are presentation concerns.
 *   - The service hop (`ChatService.markPeerMessageSeen`) only takes the
 *     primitive `messageId` (not the full entity) — confirming that the
 *     no-op decision is owned here, not at the service layer.
 */

import { ChatService } from '@/domain/services/chat/chatService';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';

export class MarkPeerMessageSeenUseCase {
  private chatService: ChatService;

  constructor(chatService: ChatService = ChatService.getInstance()) {
    this.chatService = chatService;
  }

  /**
   * Mark the given peer message as seen by issuing the Firestore transaction
   * that flips `seenByPeer: true` on both the message doc and the chat-node
   * metadata doc — unless the message already has `seenByPeer === true`, in
   * which case this is a no-op.
   *
   * @param groupChatId - The chat's group id (sorted-hashCode join of the
   *   two user ids).
   * @param message - The peer message to mark seen. Only `seenByPeer` and
   *   `documentId` are read.
   */
  async execute(groupChatId: string, message: ChatMessageEntity): Promise<void> {
    // No-op guard — parity with `chat_data_source.dart:101`
    // (`if (chatMessage.seenByPeer == false)`). Hoisted from the data layer
    // to the use case (see class JSDoc).
    if (message.seenByPeer === true) {
      return;
    }

    await this.chatService.markPeerMessageSeen(groupChatId, message.documentId);
  }
}
